import MuiPlaceIcon from '../../assets/icons/generic/mui-place.svg';
import GlobalAisDefault from '../../assets/icons/global-ais/global-ais-default.svg';
import GlobalAisStatic from '../../assets/icons/global-ais/global-ais-static.svg';
import HistoryStop from '../../assets/icons/history/stop.svg';
import HistoryWeightDecrease from '../../assets/icons/history/weight-dec.svg';
import HistoryWeightIncrease from '../../assets/icons/history/weight-inc.svg';
import DefaultIncidentMarker from '../../assets/icons/incident/default-incident-icon-marker.png';
import HighlightIncidentMarker from '../../assets/icons/incident/default-incident-icon-marker-alert.png';
import NotificationTriggerPosition from '../../assets/icons/incident/notification-trigger-position.svg';
import MaritimeIncidentBalaclava from '../../assets/icons/maritime-incidents/balaclava.svg';
import MaritimeIncidentBinoculars from '../../assets/icons/maritime-incidents/binoculars.svg';
import MaritimeIncidentBoarding from '../../assets/icons/maritime-incidents/boarding.svg';
import MaritimeIncidentDollarBag from '../../assets/icons/maritime-incidents/dollar-bag.svg';
import MaritimeIncidentExplosion from '../../assets/icons/maritime-incidents/explosion.svg';
import MaritimeIncidentMegaphone from '../../assets/icons/maritime-incidents/megaphone.svg';
import MaritimeIncidentPirate from '../../assets/icons/maritime-incidents/pirate.svg';
import MaritimeIncidentQuestionMark from '../../assets/icons/maritime-incidents/question-mark.svg';
import PortRIIcon from '../../assets/icons/ports/ri-port.svg';
import PortRICruise from '../../assets/icons/ports/ri-port-cruise.svg';
import PortRICruiseSelectedIcon from '../../assets/icons/ports/ri-port-cruise-selected.svg';
import PortRIInland from '../../assets/icons/ports/ri-port-inland.svg';
import PortRISelectedIcon from '../../assets/icons/ports/ri-port-selected.svg';
import PortIcon from '../../assets/icons/ports/wpi-port.svg';
import PortSelectedIcon from '../../assets/icons/ports/wpi-port-selected.svg';
import SelectionBrackets from '../../assets/icons/selection/selection-brackets.svg';
import buoy from '../../assets/icons/vessels/buoy.svg';
import fishingNet from '../../assets/icons/vessels/fishing-net.svg';
import PathIsoscelesTriangle from '../../assets/icons/vessels/path-triangle-isosceles.png';
import CurrentVesselWithBorder from '../../assets/icons/vessels/vessel-border.svg';
import vesselSearchArea from '../../assets/icons/vessels/vessel-navigation-icon.png';
import vesselSearchAreaAmber from '../../assets/icons/vessels/vessel-navigation-icon-orange.png';
import vesselSearchAreaRed from '../../assets/icons/vessels/vessel-navigation-icon-red.png';
import CurrentVessel from '../../assets/icons/vessels/vessel-standard.svg';
import CustomMapEvents from '../custom-map-events.enum';
import MapHelpers from '../map.utils';
import addMapImage from './layer-utils/add-image-to-layer';
import MapLayerIcon from './map-layer-icon';

namespace MapLayerManager {
  interface IGroupLayerProps {
    groupLayers?: string[];
  }

  export function getLayerIdsFromSourceGroups(sourceGroups: string[]) {
    return (
      sourceGroups?.flatMap((group) =>
        MapLayerManager.findLayersByGroupLayerId(group).map(
          (layer: { id: any }) => layer.id
        )
      ) ?? ([] as string[])
    );
  }

  // remove layer data and layer view from map
  export const destroyLayer = (layer: string) => {
    MapHelpers.removeLayer(layer);
    MapHelpers.removeSource(layer);
  };

  export const groupLayerMetaData = (
    groupLayers: string[]
  ): IGroupLayerProps => ({ groupLayers: groupLayers });

  interface LayerWithMetadata {
    metadata?: {
      groupLayers?: string[];
    };
  }

  export const findLayersByGroupLayerId = (groupLayer: string) => {
    const allLayers = MapHelpers.getAllLayers();

    const filteredLayers = allLayers.filter((layer) => {
      const { metadata } = layer as LayerWithMetadata;
      return (
        metadata &&
        Array.isArray(metadata.groupLayers) &&
        metadata.groupLayers.includes(groupLayer)
      );
    });

    return filteredLayers;
  };

  export const AddLayerinGroups = (
    layer: mapboxgl.AnyLayer,
    groupLayers: string[]
  ) => {
    (layer as LayerWithMetadata).metadata = {
      ...(layer as LayerWithMetadata).metadata,
      ...MapLayerManager.groupLayerMetaData(
        [
          (layer as LayerWithMetadata).metadata?.groupLayers || [],
          ...groupLayers,
        ].flat()
      ),
    };

    MapHelpers.addLayer(layer);

    groupLayers.forEach((groupLayer) => {
      MapLayerManager.triggerLayerGroupChange(groupLayer);
    });
  };

  const LayerGroupChangeType = (layerId: string) =>
    `${layerId}_${CustomMapEvents.GROUP_LAYER_CHANGE}`;

  export const triggerLayerGroupChange = (groupLayerId: string) =>
    MapHelpers.fire(LayerGroupChangeType(groupLayerId));

  export const onLayerGroupChange = (
    groupLayerId: string,
    callback: (ev: any) => void
  ) => {
    MapHelpers.addMapEventListener(
      LayerGroupChangeType(groupLayerId),
      null,
      callback
    );
  };

  export const removeLayerGroupChange = (
    groupLayerId: string,
    callback: (ev: any) => void
  ) => {
    MapHelpers.removeMapEventListener(
      LayerGroupChangeType(groupLayerId),
      callback
    );
  };

  export const loadMapImages = () => {
    addMapImage({
      imageImport: CurrentVessel,
      imageName: MapLayerIcon.PLAIN_VESSEL.NORMAL,
      svg: { h: 60, w: 60 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: CurrentVessel,
      imageName: MapLayerIcon.PLAIN_VESSEL.ALERT,
      svg: { h: 60, w: 60 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: CurrentVessel,
      imageName: MapLayerIcon.PLAIN_VESSEL.NEARBY,
      svg: { h: 60, w: 60 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: CurrentVesselWithBorder,
      imageName: MapLayerIcon.PLAIN_VESSEL.BORDER,
      svg: { h: 60, w: 60 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: PathIsoscelesTriangle,
      imageName: MapLayerIcon.HISTORIC_POSITION,
    });
    addMapImage({
      imageImport: HistoryStop,
      imageName: MapLayerIcon.HISTORIC_MERGED_POSITION,
      svg: { w: 40, h: 40 },
    });
    addMapImage({
      imageImport: HistoryWeightIncrease,
      imageName: MapLayerIcon.HISTORIC_DRAUGHT_CHANGE_INCREASE,
      svg: { w: 30, h: 30 },
    });
    addMapImage({
      imageImport: HistoryWeightDecrease,
      imageName: MapLayerIcon.HISTORIC_DRAUGHT_CHANGE_DECREASE,
      svg: { w: 30, h: 30 },
    });
    addMapImage({
      imageImport: NotificationTriggerPosition,
      imageName: MapLayerIcon.NOTIFICATION_TRIGGER_POSITION,
      svg: { h: 75, w: 60 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: DefaultIncidentMarker,
      imageName: MapLayerIcon.INCIDENT.DEFAULT,
    });
    addMapImage({
      imageImport: HighlightIncidentMarker,
      imageName: MapLayerIcon.INCIDENT.SELECTED,
    });
    addMapImage({
      imageImport: PortRIIcon,
      imageName: MapLayerIcon.PORT.DEFAULT_RI,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortRICruise,
      imageName: MapLayerIcon.PORT.CRUISE_RI,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortRIInland,
      imageName: MapLayerIcon.PORT.INLAND_RI,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortRISelectedIcon,
      imageName: MapLayerIcon.PORT.SELECTED_RI,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortRICruiseSelectedIcon,
      imageName: MapLayerIcon.PORT.SELECTED_CRUISE_RI,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortIcon,
      imageName: MapLayerIcon.PORT.DEFAULT,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: PortSelectedIcon,
      imageName: MapLayerIcon.PORT.SELECTED,
      svg: { h: 36, w: 28 },
    });
    addMapImage({
      imageImport: SelectionBrackets,
      imageName: MapLayerIcon.SELECTION_BRACKETS,
      svg: { h: 64, w: 64 },
    });
    addMapImage({
      imageImport: MuiPlaceIcon,
      imageName: MapLayerIcon.WEATHER.PIN,
      svg: { h: 20, w: 14 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: vesselSearchArea,
      imageName: MapLayerIcon.VESSEL_SEARCH_AREA.NORMAL,
    });
    addMapImage({
      imageImport: vesselSearchAreaAmber,
      imageName: MapLayerIcon.VESSEL_SEARCH_AREA.AMBER,
    });
    addMapImage({
      imageImport: vesselSearchAreaRed,
      imageName: MapLayerIcon.VESSEL_SEARCH_AREA.ALERT,
    });
    addMapImage({
      imageImport: buoy,
      imageName: MapLayerIcon.GLOBAL_AIS.BUOY,
      svg: { h: 48, w: 48 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: fishingNet,
      imageName: MapLayerIcon.GLOBAL_AIS.FISHING_NET,
      svg: { h: 48, w: 48 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: GlobalAisDefault,
      imageName: MapLayerIcon.GLOBAL_AIS.DEFAULT,
      svg: { w: 32, h: 48 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: GlobalAisStatic,
      imageName: MapLayerIcon.GLOBAL_AIS.STATIC,
      svg: { w: 32, h: 32 },
      convertToSDF: true,
    });
    addMapImage({
      imageImport: MaritimeIncidentQuestionMark,
      imageName: MapLayerIcon.MARITIME_INCIDENT.DEFAULT,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentBoarding,
      imageName: MapLayerIcon.MARITIME_INCIDENT.BOARDING_ATTEMPTED_ROBBERY,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentBalaclava,
      imageName: MapLayerIcon.MARITIME_INCIDENT.HIJACK_KIDNAPPING,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentExplosion,
      imageName: MapLayerIcon.MARITIME_INCIDENT.MILITARY_ACTIVITY_DAMAGE,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentPirate,
      imageName: MapLayerIcon.MARITIME_INCIDENT.MOTIVE_UNCLEAR_PIRACY,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentDollarBag,
      imageName: MapLayerIcon.MARITIME_INCIDENT.ROBBERY,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentBinoculars,
      imageName: MapLayerIcon.MARITIME_INCIDENT.SUSPICIOUS_APPROACH_ATTEMPTED,
      svg: { h: 40, w: 40 },
    });
    addMapImage({
      imageImport: MaritimeIncidentMegaphone,
      imageName: MapLayerIcon.MARITIME_INCIDENT.WARNING,
      svg: { h: 40, w: 40 },
    });
  };
}

export default MapLayerManager;
